import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { LogOut, Monitor } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { ErrorState, useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import { FormSection } from '@nucleus-commerce/admin-kit/forms';
import type { StaffSession } from '@nucleus-commerce/api-client/admin';
import { useFormat } from '@nucleus-commerce/i18n/react';
import { Badge } from '@nucleus-commerce/ui/components/badge';
import { Button } from '@nucleus-commerce/ui/components/button';
import { Skeleton } from '@nucleus-commerce/ui/components/skeleton';
import { toast } from '@nucleus-commerce/ui/components/sonner';

import { SECTION_ANCHORS } from '@/shared/navigation/section-anchors';
import { session as staffSession } from '@/shared/session/session';

import { useRevokeOtherSessions, useRevokeSession } from '../hooks/use-account-mutations';
import { useSessions } from '../hooks/use-sessions';
import { describeDevice } from '../schemas/account-schemas';

function SessionRow({ item }: { item: StaffSession }) {
  const { t } = useTranslation('account');
  const format = useFormat();
  const message = useErrorMessage();
  const navigate = useNavigate();
  const revoke = useRevokeSession();
  const queryClient = useQueryClient();
  const device = describeDevice(item.userAgent);
  const label =
    device.browser && device.os
      ? t('sessions.device', { browser: device.browser, os: device.os })
      : (device.browser ?? device.os ?? t('sessions.unknownDevice'));

  return (
    <li className="flex flex-wrap items-center justify-between gap-3 border-b py-3 last:border-b-0">
      <div className="flex min-w-0 items-start gap-3">
        <Monitor className="text-muted-foreground mt-0.5 size-4 shrink-0" aria-hidden />
        <div className="min-w-0">
          <p className="flex flex-wrap items-center gap-2 text-sm font-medium">
            {label}
            {item.current ? <Badge>{t('sessions.thisDevice')}</Badge> : null}
          </p>
          <p className="text-muted-foreground text-xs">
            {[
              item.ip,
              item.lastUsedAt
                ? t('sessions.lastUsed', { when: format.relative(item.lastUsedAt) })
                : t('sessions.started', { when: format.relative(item.createdAt) }),
            ]
              .filter(Boolean)
              .join(' · ')}
          </p>
        </div>
      </div>
      <Button
        variant="outline"
        size="sm"
        disabled={revoke.isPending}
        aria-label={t('sessions.revokeOne', { device: label })}
        onClick={() =>
          revoke.mutate(item.id, {
            onSuccess: () => {
              if (!item.current) return;
              queryClient.setQueryData(staffSession.queryKey, null);
              void navigate({ to: '/login', replace: true });
            },
            onError: (error) => toast.error(message(error)),
          })
        }
      >
        {item.current ? t('sessions.signOutHere') : t('sessions.revoke')}
      </Button>
    </li>
  );
}

/** Where this account is signed in, with this device marked and a way to end the others. */
export function SessionsCard() {
  const { t } = useTranslation('account');
  const message = useErrorMessage();
  const query = useSessions();
  const revokeOthers = useRevokeOtherSessions();
  const others = (query.data ?? []).filter((s) => !s.current).length;

  return (
    <FormSection
      title={t('sessions.title')}
      description={t('sessions.description')}
      id={SECTION_ANCHORS.sessions}
      tour="account.sessions"
    >
      {query.isPending ? (
        <Skeleton className="h-32" />
      ) : query.isError ? (
        <ErrorState error={query.error} onRetry={() => void query.refetch()} compact />
      ) : (
        <>
          <ul aria-label={t('sessions.title')}>
            {query.data.map((item) => (
              <SessionRow key={item.id} item={item} />
            ))}
          </ul>
          <div>
            <Button
              variant="outline"
              disabled={others === 0 || revokeOthers.isPending}
              onClick={() =>
                revokeOthers.mutate(undefined, {
                  onSuccess: ({ revoked }) =>
                    toast.success(t('sessions.revokedOthers', { count: revoked })),
                  onError: (error) => toast.error(message(error)),
                })
              }
            >
              <LogOut aria-hidden />
              {t('sessions.revokeOthers')}
            </Button>
          </div>
        </>
      )}
    </FormSection>
  );
}
